/**
 * Mobile bottom sheet. The same element is a right-hand panel on desktop; on
 * narrow screens it becomes a draggable sheet that snaps between three stops:
 * peek (handle + avatar row), half, and full.
 */
export type SnapName = 'peek' | 'half' | 'full';

const MQ = '(max-width: 720px)';

/** How much of the sheet stays on screen at its lowest snap — the stage sits above this. */
export const PEEK_VISIBLE = 116;

export class Sheet {
  private mq = window.matchMedia(MQ);
  private dragging = false;
  private startY = 0;
  private startOffset = 0;
  private offset = 0;
  private snap: SnapName = 'half';

  constructor(private root: HTMLElement, private handle: HTMLElement) {
    handle.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointermove', this.onMove, { passive: false });
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
    handle.addEventListener('keydown', this.onKey);
    handle.addEventListener('click', () => {
      if (!this.mobile) return;
      this.to(this.snap === 'full' ? 'peek' : this.snap === 'half' ? 'full' : 'half');
    });
    this.mq.addEventListener('change', () => this.apply(true));
    window.addEventListener('resize', () => this.apply(true));
    this.apply(true);
  }

  private get mobile(): boolean {
    return this.mq.matches;
  }

  private stops(): Record<SnapName, number> {
    const h = this.root.offsetHeight || 1;
    const vh = window.innerHeight;
    // offset = how far the sheet is pushed down, in px
    return {
      peek: Math.max(0, h - Math.min(h, PEEK_VISIBLE)),
      half: Math.max(0, h - Math.min(h, vh * 0.46)),
      full: 0,
    };
  }

  to(snap: SnapName): void {
    this.snap = snap;
    this.apply();
  }

  /** Raise the sheet at least to `snap` (used when a villager gets selected). */
  atLeast(snap: SnapName): void {
    const order: SnapName[] = ['peek', 'half', 'full'];
    if (order.indexOf(this.snap) < order.indexOf(snap)) this.to(snap);
  }

  private apply(immediate = false): void {
    if (!this.mobile) {
      this.root.style.transform = '';
      this.root.dataset.snap = 'desktop';
      return;
    }
    this.offset = this.stops()[this.snap];
    this.root.dataset.snap = this.snap;
    this.root.style.transition = immediate ? 'none' : '';
    this.root.style.transform = `translate3d(0, ${this.offset}px, 0)`;
    if (immediate) requestAnimationFrame(() => (this.root.style.transition = ''));
  }

  private onDown = (ev: PointerEvent) => {
    if (!this.mobile) return;
    this.dragging = true;
    this.startY = ev.clientY;
    this.startOffset = this.stops()[this.snap];
    this.root.style.transition = 'none';
    this.handle.setPointerCapture?.(ev.pointerId);
  };

  private onMove = (ev: PointerEvent) => {
    if (!this.dragging || !this.mobile) return;
    ev.preventDefault();
    const stops = this.stops();
    const raw = this.startOffset + (ev.clientY - this.startY);
    this.offset = Math.max(-24, Math.min(stops.peek + 60, raw));
    this.root.style.transform = `translate3d(0, ${this.offset}px, 0)`;
  };

  private onUp = () => {
    if (!this.dragging) return;
    this.dragging = false;
    this.root.style.transition = '';
    const stops = this.stops();
    let best: SnapName = 'half';
    let bestDist = Infinity;
    for (const name of ['peek', 'half', 'full'] as SnapName[]) {
      const dist = Math.abs(stops[name] - this.offset);
      if (dist < bestDist) {
        bestDist = dist;
        best = name;
      }
    }
    this.to(best);
  };

  private onKey = (ev: KeyboardEvent) => {
    if (!this.mobile) return;
    const order: SnapName[] = ['peek', 'half', 'full'];
    const i = order.indexOf(this.snap);
    if (ev.key === 'ArrowUp') this.to(order[Math.min(order.length - 1, i + 1)]);
    else if (ev.key === 'ArrowDown') this.to(order[Math.max(0, i - 1)]);
    else return;
    ev.preventDefault();
  };
}
